export interface ImageInstruction {
  domId: string;
  rotation?: number;
  [key: string]: unknown;
}

export interface MaskImage {
  rotation?: number;
  [key: string]: unknown;
}

export interface ElementWithImageInstructions {
  type: string;
  imageInstructions?: ImageInstruction[];
  maskImage?: MaskImage;
  [key: string]: unknown;
}

const normalizeRotation = (value: unknown): number => {
  if (typeof value !== "number" || Number.isNaN(value)) return 0;
  const mod = ((value % 360) + 360) % 360;
  return mod;
};

const isInstructionBased = (element: ElementWithImageInstructions): boolean => {
  return Array.isArray(element.imageInstructions);
};

const warnMissingDomId = (operation: "get" | "set", domId: unknown): void => {
  // The instruction-based branch silently returns 0 / no-ops when domId is
  // missing or unmatched, which used to mask caller bugs (typo'd or missing
  // domId would surface as "rotation = 0" then write 0 over real data on the
  // next set). Surface it in dev so the misuse fails loud at the call site
  // without breaking the production contract.
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.warn(
      `[innerImageRotationUtils] ${operation}InnerImageRotation called on instruction-based element without a matching domId (received: ${String(
        domId
      )})`
    );
  }
};

export const getInnerImageRotation = (
  element: ElementWithImageInstructions,
  domId?: string | null
): number => {
  if (!element) return 0;

  if (element.type === "textbox" || element.type === "vectortext") {
    return normalizeRotation(element.maskImage?.rotation);
  }

  if (isInstructionBased(element)) {
    if (!domId) {
      warnMissingDomId("get", domId);
      return 0;
    }
    const instruction = (element.imageInstructions as ImageInstruction[]).find(
      i => i.domId === domId
    );
    if (!instruction) {
      warnMissingDomId("get", domId);
      return 0;
    }
    return normalizeRotation(instruction.rotation);
  }

  return 0;
};

export const setInnerImageRotation = <T extends ElementWithImageInstructions>(
  element: T,
  domId: string | null | undefined,
  rotation: number
): T => {
  const normalized = normalizeRotation(rotation);

  if (element.type === "textbox" || element.type === "vectortext") {
    return {
      ...element,
      maskImage: {
        ...(element.maskImage || {}),
        rotation: normalized
      }
    };
  }

  if (isInstructionBased(element)) {
    if (!domId) {
      warnMissingDomId("set", domId);
      return element;
    }
    const instructions = element.imageInstructions as ImageInstruction[];
    const matched = instructions.some(i => i.domId === domId);
    if (!matched) {
      warnMissingDomId("set", domId);
      return element;
    }
    return {
      ...element,
      imageInstructions: instructions.map(instruction =>
        instruction.domId === domId
          ? { ...instruction, rotation: normalized }
          : instruction
      )
    };
  }

  return element;
};
